import type {
  PointerEvent as ReactPointerEvent,
  TouchEvent as ReactTouchEvent,
  Touch,
} from "react";

const GHOST_CLICK_GUARD_MS = 450;
const TOUCH_SCROLL_THRESHOLD_PX = 8;
const TOUCH_START_X_ATTR = "data-combobox-touch-start-x";
const TOUCH_START_Y_ATTR = "data-combobox-touch-start-y";
const TOUCH_MOVED_ATTR = "data-combobox-touch-moved";

let activeGhostClickGuardCleanup: (() => void) | null = null;

/** Let an expanded combobox consume Escape before its enclosing modal. */
function preserveComboboxEscape(event: KeyboardEvent): void {
  if (
    event.target instanceof Element &&
    event.target.closest('[role="combobox"][aria-expanded="true"]')
  ) {
    event.preventDefault();
  }
}

/**
 * Absorb the synthetic click iOS Safari fires after touchstart on a listbox option.
 * Mount inside the same modal root as the menu (drawer/dialog), not document.body.
 */
function armComboboxGhostClickGuard(container: HTMLElement): () => void {
  activeGhostClickGuardCleanup?.();

  const shield = document.createElement("div");
  shield.setAttribute("data-combobox-ghost-shield", "");
  shield.setAttribute("aria-hidden", "true");
  Object.assign(shield.style, {
    position: container === document.body ? "fixed" : "absolute",
    inset: "0",
    zIndex: "55",
    pointerEvents: "auto",
    touchAction: "none",
    background: "transparent",
  });

  const stopEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  for (const type of ["click", "touchend", "pointerup", "mousedown"] as const) {
    shield.addEventListener(type, stopEvent, true);
  }

  const onDocumentClickCapture = (event: Event) => {
    stopEvent(event);
  };

  container.appendChild(shield);
  document.addEventListener("click", onDocumentClickCapture, true);
  document.addEventListener("touchend", onDocumentClickCapture, true);

  const cleanup = () => {
    window.clearTimeout(timeoutId);
    shield.remove();
    document.removeEventListener("click", onDocumentClickCapture, true);
    document.removeEventListener("touchend", onDocumentClickCapture, true);
    if (activeGhostClickGuardCleanup === cleanup) {
      activeGhostClickGuardCleanup = null;
    }
  };

  const timeoutId = window.setTimeout(cleanup, GHOST_CLICK_GUARD_MS);
  activeGhostClickGuardCleanup = cleanup;

  return cleanup;
}

function clearComboboxGhostClickGuard(): void {
  activeGhostClickGuardCleanup?.();
}

/**
 * Select a listbox option on touch/pointer down so trigger blur cannot
 * dismiss the menu before selection completes (iOS Safari + modal drawers).
 */
function onListboxOptionPointerDown(
  event: ReactPointerEvent<HTMLElement>,
  select: () => void,
): void {
  event.preventDefault();
  event.stopPropagation();

  select();
}

function firstTouch(event: ReactTouchEvent<HTMLElement>): Touch | null {
  return (
    event.touches.item?.(0) ??
    event.touches[0] ??
    event.changedTouches.item?.(0) ??
    event.changedTouches[0] ??
    null
  );
}

function clearTouchState(target: HTMLElement): void {
  target.removeAttribute(TOUCH_START_X_ATTR);
  target.removeAttribute(TOUCH_START_Y_ATTR);
  target.removeAttribute(TOUCH_MOVED_ATTR);
}

/**
 * Track touch intent without cancelling native scroll. Selection happens on
 * touchend only if the finger stayed within a small tap threshold.
 */
function onListboxOptionTouchStart(event: ReactTouchEvent<HTMLElement>): void {
  const touch = firstTouch(event);
  if (!touch) return;

  event.stopPropagation();
  const target = event.currentTarget;
  target.setAttribute(TOUCH_START_X_ATTR, String(touch.clientX));
  target.setAttribute(TOUCH_START_Y_ATTR, String(touch.clientY));
  target.removeAttribute(TOUCH_MOVED_ATTR);
}

function onListboxOptionTouchMove(event: ReactTouchEvent<HTMLElement>): void {
  const touch = firstTouch(event);
  if (!touch) return;

  const target = event.currentTarget;
  const startX = Number(target.getAttribute(TOUCH_START_X_ATTR));
  const startY = Number(target.getAttribute(TOUCH_START_Y_ATTR));
  if (!Number.isFinite(startX) || !Number.isFinite(startY)) return;

  const moved =
    Math.abs(touch.clientX - startX) > TOUCH_SCROLL_THRESHOLD_PX ||
    Math.abs(touch.clientY - startY) > TOUCH_SCROLL_THRESHOLD_PX;
  if (moved) {
    target.setAttribute(TOUCH_MOVED_ATTR, "true");
  }
}

function onListboxOptionTouchEnd(
  event: ReactTouchEvent<HTMLElement>,
  select: () => void,
  options?: { ghostClickRoot?: HTMLElement | null },
): void {
  const target = event.currentTarget;
  const moved = target.getAttribute(TOUCH_MOVED_ATTR) === "true";
  clearTouchState(target);

  if (moved) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const root = options?.ghostClickRoot;
  if (root) {
    armComboboxGhostClickGuard(root);
  }

  select();
}

export {
  clearComboboxGhostClickGuard,
  onListboxOptionPointerDown,
  onListboxOptionTouchEnd,
  onListboxOptionTouchMove,
  onListboxOptionTouchStart,
  preserveComboboxEscape,
};
